/**
 * failure.mjs — the five families, and the one symptom they all produce.
 *
 * FOUNDER, 2026-08-14: "ch7 next".
 *
 * ── THIS CHAPTER IS THE OTHER SIX, SO THE LAB IS THE OTHER FIVE LABS ──────────────────────────
 * §2 is titled "The five families of failure — a tour back through Days 1–6", and every family
 * names the day that taught it:
 *
 *     Code failure     → Day 4, what code is           → the blocks lab
 *     Courier failure  → Day 2, how the internet moves → the courier lab
 *     Data failure     → Day 3, what data is           → the shapes lab
 *     Identity failure → Day 5, identity and trust     → the identity lab
 *     Storage failure  → Day 6, storage vs memory      → the persist lab
 *
 * That mapping is his, not a convenience: a lab was built for each of those five days before this
 * chapter was reached, so the learner has already WATCHED every family fail. The synthesis is real
 * rather than decorative, and the lab links back to the exact instrument where each was seen.
 *
 * ── THE POINT IS THAT THE SYMPTOM IS ALWAYS THE SAME ──────────────────────────────────────────
 * §2: "Most real failures are combinations of two or three families. A bad sync (storage) of a
 * stale API key (identity) over a flaky network (courier) — three families compounding into one
 * user-visible 'the app isn't working.'" So whatever a learner selects, the symptom this engine
 * returns is that one sentence. Producing a different symptom per family would teach that you can
 * diagnose from the outside, which is the belief §3 exists to break.
 */

export const SOURCE_1 = 'Code101 module 07 §1 "All software fails — the principle, not the exception"';
export const SOURCE_2 = 'Code101 module 07 §2 "The five families of failure — a tour back through Days 1–6"';
export const SOURCE_3 = 'Code101 module 07 §3 "How you know — observation is the first discipline"';
export const SOURCE_4 = 'Code101 module 07 §4 "What the system does next — four responses"';
export const SOURCE_7 = 'Code101 module 07 §7 "Who pays — and why someone always does"';

/** §1, the sentence the chapter opens on. */
export const NOT_TWO_STATES = 'software does not have two states (working / broken); it has many states, and the mature question is which failures we have seen and what we did about them.';
export const SINGLE_MISTAKE = 'The single mistake most non-technical people make about software is to imagine it as something that works or is broken.';

/**
 * The five, in his order, each carrying the day it belongs to and the chapter of this course where
 * the learner already watched it happen.
 */
export const FAMILIES = Object.freeze([
  { id: 'code', name: 'Code failure', day: 4, chapter: 4, lab: 'the blocks lab',
    what: 'The recipe itself is wrong.',
    detail: 'Syntax errors prevent the program from starting. Semantic errors let it start and produce wrong results.',
    seen: 'You turned an add into a multiply and watched the machine produce 43,290 without complaint.' },
  { id: 'courier', name: 'Courier failure', day: 2, chapter: 2, lab: 'the courier lab',
    what: 'The request never arrived, the answer never came back, the round-trip timed out, the connection was reset.',
    detail: 'Three modes from Day 2: no connection, slow connection, remote machine down.',
    seen: 'You stopped the walk at DNS, at the journey, and at the far machine.' },
  { id: 'data', name: 'Data failure', day: 3, chapter: 3, lab: 'the shapes lab',
    what: 'The data was malformed, missing fields, in the wrong shape, contradicting itself, in the wrong character encoding, or in a unit nobody agreed on (pounds vs kilograms is a real, recurring cause of expensive software failures).',
    detail: 'Garbage in, garbage out — and most "AI is wrong" reports are actually correct AI processing of broken data.',
    seen: 'You asked a paragraph a question it could not be asked.' },
  { id: 'identity', name: 'Identity failure', day: 5, chapter: 5, lab: 'the identity lab',
    what: 'The user could not sign in. The token expired mid-action. The authorization check failed.',
    detail: 'These are often the hardest failures to debug because the system partially works — most things go through, a specific thing does not.',
    seen: 'You watched a correctly signed-in person be handed a stranger\'s list.' },
  { id: 'storage', name: 'Storage failure', day: 6, chapter: 6, lab: 'the persist lab',
    what: 'The state in memory disagreed with the state in storage.',
    detail: 'These are the slowest, sneakiest failures — usually invisible until someone notices a number that looks wrong.',
    seen: 'You typed into the desk and pulled the power before the cabinet caught up.' },
]);

/** §2's own compound example, quoted, so the lab has a worked case that is his and not mine. */
export const HIS_COMPOUND = Object.freeze({
  families: ['storage', 'identity', 'courier'],
  quote: 'A bad sync (storage) of a stale API key (identity) over a flaky network (courier) — three families compounding into one user-visible "the app isn\'t working."',
});

/** The one symptom. §2 puts it in quotes and it is the only symptom this engine will ever return. */
export const THE_SYMPTOM = 'the app isn\'t working';

/** §3's four channels, quoted. */
export const CHANNELS = Object.freeze([
  { id: 'logs', name: 'Logs', quote: 'every meaningful event the program writes down, with a timestamp, in plain text. Logs are the program\'s diary.' },
  { id: 'monitoring', name: 'Monitoring', quote: 'automated systems that watch the program from outside, measuring how fast it responds, how often it returns errors, how much memory it is using, how full its disks are.' },
  { id: 'alerts', name: 'Alerts', quote: 'automatic messages (SMS, email, push notification, page) when one of the monitors crosses a threshold.' },
  { id: 'reports', name: 'User reports', quote: 'what the people using the system tell you.' },
]);
export const OBSERVATION = 'observation is what turns failure from a catastrophe into a fact.';
export const NO_EYES = 'A system where the user is always the first to notice is a system without observation — and that system will fail expensively, repeatedly, until someone fixes the absence of eyes.';

/** §4's four responses, quoted. */
export const RESPONSES = Object.freeze([
  { id: 'retry', name: 'Retry', quote: 'try the same thing again, often with a small delay.' },
  { id: 'degrade', name: 'Fail gracefully (degrade)', quote: 'keep going, but with less.' },
  { id: 'loud', name: 'Fail loud', quote: 'stop, surface the failure, alert someone.' },
  { id: 'rollback', name: 'Roll back', quote: 'undo whatever just happened.' },
]);
export const ALL_FOUR = 'Most well-built systems use all four, situationally.';

/** §7. */
export const PAYERS = Object.freeze(['The user pays.', 'The engineer pays.', 'The business pays.', 'The regulator pays (and then bills back to the business).']);
export const SOMEONE_PAYS = 'Every software failure has a cost. The mature question is not was there a cost — there always was — but who paid it.';

/**
 * Compose an incident. Whatever is chosen, the symptom is the same sentence — that is the whole
 * design. What CHANGES is the diagnosis: which families, which days they came from, and how many
 * are compounding.
 *
 * It deliberately does not tell you which response to use. §4 says "Most well-built systems use all
 * four, situationally" — a lab that answered "your failure is X, therefore do Y" would invent a
 * decision procedure the module does not give, and the four responses are exactly the judgement a
 * learner is meant to be forming.
 */
export function incident(ids = []) {
  const chosen = FAMILIES.filter((f) => ids.includes(f.id));
  return {
    families: chosen,
    /* ALWAYS the same, and never derived from the selection. */
    symptom: THE_SYMPTOM,
    count: chosen.length,
    compound: chosen.length > 1,
    days: chosen.map((f) => f.day).sort((a, b) => a - b),
    /* §5: "The tests prove the things you thought to check. The world will check the things you
       forgot." Nothing here scores the learner's choice. */
    verdict: null,
  };
}

export const TESTS_PROVE = 'The tests prove the things you thought to check. The world will check the things you forgot.';
export const AI_FAILS = 'AI is software. Software fails. AI fails specifically in these five new ways, on top of the old five.';
export const SOURCE_5 = 'Code101 module 07 §5 "Verification — testing is a recipe that checks other recipes"';
export const SOURCE_6 = 'Code101 module 07 §6 "AI fails too — the new species, the new failure modes"';
